// -------------------------
// React
// -------------------------
import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";

// -------------------------
// Third-party libraries
// -------------------------
import { Button, Col, Row, Tab, Tabs, Card } from "react-bootstrap";
import {
  Camera,
  Check,
  CheckCircle,
  Joystick,
  LightningCharge,
  Radar,
  Reception0,
  Reception1,
  Reception2,
  Reception3,
  Reception4,
  ShieldCheck,
} from "react-bootstrap-icons";
import domtoimage from "dom-to-image-more";
import isEqual from "lodash/isEqual";

// -------------------------
// Internal components
// -------------------------
import {
  Compass,
  GNSSStatusTable,
  InterferenceEventComponent,
  SpectrogramComponent,
  StatusListItemComponent,
  InterferenceAlertCard,
} from "components";

// -------------------------
// Utilities / helpers
// -------------------------
import { formatFrequency } from "../../utils/helpers/formatters";
import { useGnssApi, useWebSocket, useAuth } from "utils/hooks";

import { StationStatus } from "../../utils/constants";
import { COLORS } from "utils/colors";

/**
 * SpectrumTabs renders spectrum and interference data for a GNSS station
 * in a tabbed interface with real-time updates via WebSocket.
 * @memberof module:components/tabs
 * @param {Object} props - Component props
 * @param {Object} props.stationInfo - Information about the GNSS station
 * @param {string} props.stationInfo.identifier - Station unique identifier
 * @param {string} props.stationInfo.type - Station type (e.g. PERFORMANCE, LOCALIZATION)
 * @param {Object} props.stationInfo.location - Station location
 * @param {number} props.stationInfo.location.lon - Longitude coordinate
 * @param {number} props.stationInfo.location.lat - Latitude coordinate
 * @param {string} props.stationInfo.sisStationHostname - Hostname for the receiver page
 * @param {number} props.stationInfo.sisStationPort - Port for the receiver page
 * @param {boolean} props.isExpanded - Whether to show the expanded interference events table
 *
 * @returns {JSX.Element} The rendered SpectrumTabs component
 */
const SpectrumTabs = ({ stationInfo, isExpanded }) => {
  // -------------------------
  // Hooks
  // -------------------------
  const {selected_tenant_id} = useAuth();
  const api = useGnssApi();
  const navigate = useNavigate();
  const [qualityIndicators, setQualityIndicators] = useState({
    rfPower: null,
    mainSignals: null,
    trackedSat: null,
    nrSV: null,
  });
  const [spectrumData, setSpectrumData] = useState(null);
  const [monitoredBands, setMonitoredBands] = useState(
    stationInfo.monitoredBands || [],
  );
  const [interferenceEventsCurrent, setInterferenceEventsCurrent] = useState(
    [],
  );
  const [interferenceEventsQueue, setInterferenceEventsQueue] = useState([]);
  const [interferenceAlerts, setInterferenceAlerts] = useState([]);
  const [performanceData, setPerformanceData] = useState([]);
  const [stationStatus, setStationStatus] = useState({
    code: StationStatus.state.CONNECTING,
    message: "Connecting",
  });
  const [initialData, setInitialData] = useState(null);

  const [warning, setWarning] = useState("");

  const [isConnected, subscribe, unsubscribe] = useWebSocket();

  const spectrogramTabRef = useRef(null);
  const spectrogramComponentRef = useRef(null);

  // -------------------------
  // Constants
  // -------------------------
  const DISPLAYED_ALERTS_LIMIT = 3;
  const channelName = `${selected_tenant_id}-${stationInfo.identifier}-interferences`;

  // -------------------------
  // Effects
  // -------------------------
  // Subscribe to WebSocket channel when connected
  useEffect(() => {
    if (isConnected) subscribe(channelName, handleReceiveCallbackMessage);
    return () => unsubscribe(channelName);
  }, [isConnected, subscribe, unsubscribe]);

  // Initial fetch of spectrogram image and alerts
  useEffect(() => {
    getAlertsByStationId(DISPLAYED_ALERTS_LIMIT);
  }, []);

  useEffect(() => {
    if (
      stationStatus.code === StationStatus.state.CONNECTING ||
      stationStatus.code === StationStatus.state.DISCONNECTED
    ) {
      setQualityIndicators((prev) => ({
        ...prev,
        rfPower: null,
        mainSignals: null,
        trackedSat: null,
        nrSV: null,
      }));
    }
  }, [stationStatus]);

  // -------------------------
  // Callbacks
  // -------------------------
  /**
   * Handles incoming WebSocket messages with spectrum data.
   * Parses the message and updates state with new spectrum and interference data.
   *
   * @param {Object} stompMessage - The received STOMP message object
   */
  const handleReceiveCallbackMessage = useCallback(
    (stompMessage) => {
      const wsPackage = JSON.parse(stompMessage.body);

      let payload = wsPackage.data;

      if (!payload) return;

      setQualityIndicators((prev) => ({
        ...prev,
        ...(payload.rfPower !== undefined && { rfPower: payload.rfPower }),
        ...(payload.mainSignals !== undefined && {
          mainSignals: payload.mainSignals,
        }),
        ...(payload.trackedSat !== undefined && {
          trackedSat: payload.trackedSat,
        }),
        ...(payload.nrSV !== undefined && { nrSV: payload.nrSV }),
      }));

      const { timestamp, spectrogramDataset } = payload;
      setSpectrumData({ timestamp, spectrogramDataset });

      const requiredKeys = ["name", "centraFreq", "bandwidth"]; // example

      const isValid =
        Array.isArray(payload.monitoredBands) &&
        payload.monitoredBands.length > 0 &&
        payload.monitoredBands.every(
          (band) =>
            requiredKeys.every((key) => band.hasOwnProperty(key)) &&
            Object.values(band).every((value) => value != null),
        );

      if (
        !isEqual(payload.monitoredBands, monitoredBands) &&
        isValid &&
        payload.monitoredBands.length > 0
      ) {
        setMonitoredBands(payload.monitoredBands);
      }

      if (payload.activeAlerts?.length > 0) {
        enqueueAlerts(payload.activeAlerts);
      }

      setPerformanceData(payload.performance);

      const events = mapInterferenceEvents(payload);

      setInterferenceEventsCurrent(events);
      if (events.length) enqueueEvent(events);
    },
    [monitoredBands, stationInfo.type],
  );

  /**
   * Adds new alert events to the queue and keeps only the most recent 4 events.
   *
   * @param {Array} alerts - Array of new alert events objects to add
   */
  const enqueueAlerts = useCallback((alerts) => {
    setInterferenceAlerts((prevAlerts) => {
      let updatedAlerts = Array.isArray(prevAlerts) ? prevAlerts : [prevAlerts];
      for (const newAlert of alerts) {
        const index = updatedAlerts.findIndex(
          (alert) => alert.id === newAlert.id,
        );
        if (index === -1) {
          // Not found → push front
          updatedAlerts = [newAlert, ...updatedAlerts];
        } else {
          // Found → update in place
          updatedAlerts = updatedAlerts.map((alert, i) =>
            i === index ? { ...alert, ...newAlert } : alert,
          );
        }
      }

      const stateOrder = {
        FIRING: 0,
        STOPPED: 1,
      };

      updatedAlerts = updatedAlerts.sort((a, b) => {
        return (stateOrder[a.state] ?? 99) - (stateOrder[b.state] ?? 99);
      });

      if (updatedAlerts.length > DISPLAYED_ALERTS_LIMIT) {
        updatedAlerts = updatedAlerts.slice(0, DISPLAYED_ALERTS_LIMIT);
      }

      return updatedAlerts;
    });
  }, []);

  /**
   * Adds new interference events to the queue and keeps only the most recent 25 events.
   *
   * @param {Array} newEvents - Array of new interference event objects to add
   */
  const enqueueEvent = useCallback((interferenceData) => {
    setInterferenceEventsQueue((prevQueue) => {
      const newQueue = [...interferenceData, ...prevQueue];
      return newQueue.slice(0, 2);
    });
  }, []);

  /**
   * Fetch spectrogram image for the station
   */
  const getSpectrogramImage = useCallback(async () => {
    try {
      //console.log("get spectrogram");
      const response = await api.getSpectrogramImageByStationId(
        stationInfo.groupNames[0],
        stationInfo.identifier,
        452,
        300,
      );

      if (response.data) setInitialData(response.data);

      return response;
    } catch (e) {
      // console.log(e);
    }
  }, [api]);

  /**
   * Fetch latest interference alerts by station ID
   */
  const getAlertsByStationId = useCallback(
    async (limit) => {
      try {
        const response = await api.getAlertsByStationId(
          stationInfo.groupNames[0],
          stationInfo.identifier,
          limit,
        );

        response?.data && setInterferenceAlerts(response.data);
        return response;
      } catch (e) {
        console.log(e);
      }
    },
    [api],
  );

  const handleViewMore = (alert) => {

    navigate("/gnss/interference-report", {
      state: {
        stationIdentifier: stationInfo.identifier,
        alertId: alert.id,
        alertState: alert.state
      },
    });
  };

  /**
   * Take a snapshot of the spectrogram tab
   */
  const handleSave = async (stationId) => {
    if (!spectrogramTabRef.current) return;

    const originalWidth = spectrogramTabRef.scrollWidth;
    const originalHeight = spectrogramTabRef.scrollHeight;
    // Get device pixel ratio (accounts for zoom)
    const scale = 1;
    domtoimage
      .toPng(spectrogramTabRef.current, {
        width: originalWidth,
        height: originalHeight,
        style: {
          transform: `scale(1)`, // ignore any CSS scaling
          transformOrigin: "top left",
          width: `${originalWidth * 2}px`,
          height: `${originalHeight}px`,
        },
      })
      .then((dataUrl) => {
        const link = document.createElement("a");
        const now = new Date();

        const formattedDate = now
          .toISOString() // "2025-10-06T15:45:12.345Z"
          .replace(/[:\-.Z]/g, "") // remove separators
          .replace(/[T]/g, "_")
          .slice(0, 15); // "20251006_154512"

        link.download = `${stationId}_${formattedDate}_snapshot.png`;
        link.href = dataUrl;
        link.click();
      });
  };

  // -------------------------
  // Derived / Memoized Data
  // -------------------------
  const filteredEvents = useMemo(() => {
    if (!spectrumData || !monitoredBands?.length) return [];
    return interferenceEventsCurrent
      ?.flat()
      .filter(
        (e) =>
          Object.keys(e).length &&
          e.frequencyValue >=
            monitoredBands[0].centralFreq - monitoredBands[0].bandwidth / 2 &&
          e.frequencyValue <=
            monitoredBands[monitoredBands.length - 1].centralFreq +
              monitoredBands[monitoredBands.length - 1].bandwidth / 2,
      );
  }, [spectrumData, monitoredBands, stationInfo.type]);

  // -------------------------
  // HELPERS
  // -------------------------
  const mapInterferenceEvents = (payload) =>
    payload?.interferenceEvents?.interferenceDetails?.map((i) => ({
      timestamp: new Date(payload.timestamp).toISOString().substring(11, 19),
      frequencyLabel: formatFrequency(i.interferenceFrequency),
      frequencyValue: i.interferenceFrequency,
      ...(i.interferenceBandwidth && {
        bandwidthValue: i.interferenceBandwidth,
        bandwidthLabel: formatFrequency(i.interferenceBandwidth),
      }),
      ...(i.bearing !== null &&
        i.bearing >= 0 &&
        i.bearing <= 360 && { bearing: i.bearing }),
      isMitigated: i.autoMitigation,
      isSpoofing: payload.interferenceEvents.spoofingDetected,
    })) || [];

  // -------------------------
  // Render helpers
  // -------------------------

  const renderNoAlerts = () => {
    return (
      <Card
        className="m-2 p-0 border-danger"
        style={{ background: "transparent" }}
      >
        <Card.Body
          style={{ background: COLORS.ligthGrey }}
          className="rounded border-danger"
        >
          <div className="small p-0 m-0 d-flex align-items-center justify-content-center">
            <Col>
              <ShieldCheck
                style={{ height: "5em", width: "5em" }}
                className="me-1 text-success"
              ></ShieldCheck>
            </Col>
            <span style={{ color: COLORS.greyFontColor }}>
              Station{" "}
              <b>
                {stationInfo.name} ({stationInfo.identifier})
              </b>{" "}
              located at{" "}
              <i>
                [{stationInfo.location.lat}, {stationInfo.location.lon}]
              </i>{" "}
              maintained a clean GNSS signal, with{" "}
              <b>
                <i>no interference</i>
              </b>{" "}
              or{" "}
              <b>
                <i>spoofing</i>
              </b>{" "}
              events detected over the past 30 days. All monitored parameters
              remained within international thresholds, indicating stable signal
              quality and nominal environmental conditions throughout the
              observation period.
            </span>
          </div>
        </Card.Body>
      </Card>
    );
  };

  const renderIndicators = () => {
    const formatOutOfTen = (value) =>
      value === null || value === undefined ? "N/A" : `${value}/10`;

    const getReceptionIndicator = (value) => {
      if (value == null) {
        return { icon: Reception0, color: "secondary" }; // or "N/A" state
      }

      const v = Number(value);

      if (v === 0) return { icon: Reception0, color: "danger" };
      if (v <= 3) return { icon: Reception1, color: "danger" };
      if (v <= 6) return { icon: Reception2, color: "warning" };
      if (v <= 8) return { icon: Reception3, color: "warning" };
      if (v <= 10) return { icon: Reception4, color: "success" };

      return { icon: Reception0, color: "secondary" };
    };

    const items = [
      {
        indicator: getReceptionIndicator(qualityIndicators.rfPower),
        value: formatOutOfTen(qualityIndicators.rfPower),
        label: "Main RF power",
      },
      {
        indicator: getReceptionIndicator(qualityIndicators.mainSignals),
        value: formatOutOfTen(qualityIndicators.mainSignals),
        label: "Main signals",
      },
      {
        icon: Radar,
        value: qualityIndicators.trackedSat || "N/A",
        label: "Tracked satellites",
      },
      {
        icon: Joystick,
        value: qualityIndicators.nrSV || "N/A",
        label: "Satellites used for PVT",
      },
    ];
    return (
      <Card
        className="m-2 p-0 border-danger"
        style={{ background: "transparent" }}
      >
        <Card.Body
          style={{ background: COLORS.ligthGrey }}
          className="rounded border-danger"
        >
          <div className="ms-2 d-flex text-center">
            {items.map((item, index) => {
              const Icon = item.icon;
              const Indicator = item.indicator;

              return (
                <div key={index} className="flex-fill">
                  <div className="d-flex flex-column align-items-center">
                    <small className="mb-1">{item.label}</small>

                    {Indicator ? (
                      <Indicator.icon
                        size={20}
                        className={`text-${Indicator.color}`}
                      />
                    ) : (
                      <Icon size={20} />
                    )}

                    <small className="mt-1">{item.value}</small>
                  </div>
                </div>
              );
            })}
          </div>
        </Card.Body>
      </Card>
    );
  };

  const renderTabs = () => {
    return monitoredBands.map((band, index) => {
      if (!band) return;

      const dataset = spectrumData?.spectrogramDataset?.find(
        (ds) => ds.localOscillatorFrequency === band.centralFreq,
      );

      const bandwidth = band.bandwidth / 1e6;
      const lowerFreq = band.centralFreq / 1e6 - bandwidth / 2;
      const upperFreq = band.centralFreq / 1e6 + bandwidth / 2;

      return (
        <Tab
          key={index}
          eventKey={`band${index}`}
          title={`${band.name} ${band.centralFreq / 1e6} MHz`}
        >
          <div>
            <SpectrogramComponent
              ref={spectrogramComponentRef}
              band={band.centralFreq}
              data={dataset}
              initialData={initialData}
              lowerFrequency={lowerFreq}
              upperFrequency={upperFreq}
            />
          </div>
          <div style={{ marginTop: 10 }} id="legend">
            <Row>
              <Col md={{ span: 6 }} style={{ height: 15, textAlign: "left" }}>
                {`Central Freq.: ${formatFrequency(band.centralFreq)}`}
              </Col>
            </Row>
            <Row>
              <Col md={{ span: 6 }} style={{ height: 15, textAlign: "left" }}>
                {`Bandwidth: ${formatFrequency(band.bandwidth)}`}
              </Col>
            </Row>
            {}
          </div>
        </Tab>
      );
    });
  };

  const renderFooter = () => {
    return (
      <div
        className="tab-footer pt-0"
        style={{
          height: "5em",
        }}
      >
        <div id="legend">
          <Row className="p-0 m-0">
            <span
              className={"p-0 m-0"}
              style={{
                display: "inline-block",
                height: "1em",
                lineHeight: "1em",
                whiteSpace: "normal",
              }}
            >
              Current timestamp:{" "}
              {spectrumData?.timestamp &&
              stationStatus.code !== StationStatus.state.DATA_UNAVAILABLE &&
              stationStatus.code !== StationStatus.state.DISCONNECTED &&
              stationStatus.code !== StationStatus.state.CONNECTING
                ? new Date(spectrumData.timestamp).toISOString()
                : "N/A"}
            </span>

            <span
              className={`p-0 m-0 ${
                warning.code !== StationStatus.warning.NO_WARNING
                  ? "text-warning"
                  : ""
              }`}
              style={{
                display: "inline-block",
                height: "1em",
                lineHeight: "1em",
                whiteSpace: "normal",
              }}
            >
              {warning.code !== StationStatus.warning.NO_WARNING
                ? warning.message
                : ""}
            </span>

            {spectrumData &&
              stationStatus.code !== StationStatus.state.DATA_UNAVAILABLE &&
              stationStatus.code !== StationStatus.state.DISCONNECTED &&
              stationStatus.code !== StationStatus.state.CONNECTING &&
              interferenceEventsQueue.length === 0 && (
                <span
                  className={"p-0 m-0 text-success"}
                  style={{
                    display: "inline-block",
                    height: "1em",
                    lineHeight: "1em",
                    whiteSpace: "normal",
                  }}
                >
                  <b>No interferences detected</b>
                </span>
              )}

            {(() => {
              const hasData =
                spectrumData &&
                stationStatus.code !== StationStatus.state.DATA_UNAVAILABLE &&
                stationStatus.code !== StationStatus.state.DISCONNECTED &&
                stationStatus.code !== StationStatus.state.CONNECTING &&
                interferenceEventsQueue.length > 0;

              if (!hasData) return null;

              return (
                <>
                  <span
                    style={{
                      display: "inline-block",
                      height: "1em",
                      lineHeight: "1em",
                      whiteSpace: "normal",
                    }}
                    className="p-0 m-0 text-danger"
                  >
                    <b>Last detected interference events</b>
                  </span>

                  {interferenceEventsQueue.slice(0, 2).map((event) => (
                    <span
                      style={{
                        display: "inline-block",
                        height: "1em",
                        lineHeight: "1em",
                        whiteSpace: "normal",
                      }}
                      className="p-0 m-0 text-danger"
                    >
                      {event.timestamp && <>{event.timestamp} </>}
                      {event.frequencyLabel && (
                        <>Frequency {event.frequencyLabel} </>
                      )}
                      {event.bandwidthLabel && (
                        <>with bandwidth {event.bandwidthLabel} </>
                      )}
                      {event.bearing && <>and bearing {event.bearing} &#176;</>}
                    </span>
                  ))}
                </>
              );
            })()}
          </Row>
        </div>
      </div>
    );
  };

  // -------------------------
  // JSX
  // -------------------------
  return (
    <Row className="m-0 p-0">
      <Col
        className="p-0 m-0"
        style={{ width: "auto", minWidth: 700, maxWidth: 728 }}
      >
        <Row className="p-0 m-0">
          <Col
            id="tabspectrogram"
            ref={spectrogramTabRef}
            className="d-flex flex-column"
            md={{ span: 9 }}
            style={{ height: "100%" }}
          >
            <div style={{ flexGrow: 1 }}>
              {monitoredBands?.length > 0 ? (
                <Tabs variant="pills" defaultActiveKey="band0">
                  {renderTabs()}
                </Tabs>
              ) : (
                <div
                  style={{ background: COLORS.ligthGrey, minHeight: 603.61 }}
                />
              )}
            </div>

            {renderFooter()}

            <div className="mt-3">
              <GNSSStatusTable
                performanceTableData={
                  stationStatus.code !== StationStatus.state.DATA_UNAVAILABLE &&
                  stationStatus.code !== StationStatus.state.DISCONNECTED &&
                  stationStatus.code !== StationStatus.state.CONNECTING
                    ? performanceData
                    : []
                }
              />
            </div>
          </Col>
          <Col
            id="interferenceevent"
            md={{ span: 3 }}
            className="d-flex flex-column"
          >
            <div>
              <Button
                size="sm"
                className="m-0 d-flex align-items-center justify-content-center text-center"
                variant="outline-primary"
                style={{ width: "100%" }}
                onClick={() => handleSave(stationInfo.identifier)}
              >
                <Camera size={15} className="me-1" /> Take snapshot
              </Button>
              <InterferenceEventComponent
                stationCoords={[
                  stationInfo.location.lon,
                  stationInfo.location.lat,
                ]}
                stationId={stationInfo.identifier}
                onStatusChanged={(status, warning) => {
                  setWarning(warning);
                  setStationStatus(status);
                }}
                status={stationStatus}
                tenantCountry={stationInfo.identifier === "GRZ0" ? "AU" : "RO"}
              />

              <Row>
                <StatusListItemComponent
                  interferences={filteredEvents}
                  bands={monitoredBands}
                  stationStatus={stationStatus}
                />
              </Row>
            </div>
            <div className="mt-auto">
              <Row>
                {stationInfo?.sis?.hostname &&
                (stationInfo.type === "PERFORMANCE" ||
                  stationInfo.type === "PERFORMANCE_AND_LOCALIZATION") ? (
                  <></>
                ) : stationInfo.type !== "LOCALIZATION" ? (
                  <small>
                    <span className="text-danger text-justify">
                      <b>Warning:</b> No hostname associated
                    </span>
                  </small>
                ) : (
                  <></>
                )}
              </Row>
              {stationInfo.type === "PERFORMANCE_AND_LOCALIZATION" &&
                stationInfo?.sis?.hostname && (
                  <>
                    <Row>
                      <small>
                        Interference directions:{" "}
                        <div
                          style={{
                            minHeight: "4rem", // occupies vertical space
                            minWidth: "100px", // optional: ensures horizontal presence
                            maxWidth: "200px",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {
                            interferenceEventsCurrent
                              ?.flat() // Flatten the array of arrays into a single array
                              ?.filter(
                                (event) =>
                                  Object.keys(event).length > 0 &&
                                  event.frequencyValue >=
                                    monitoredBands[0]?.centralFreq -
                                      monitoredBands[0]?.bandwidth / 2 &&
                                  event.frequencyValue <=
                                    monitoredBands[monitoredBands.length - 1]
                                      ?.centralFreq +
                                      monitoredBands[monitoredBands.length - 1]
                                        ?.bandwidth /
                                        2 &&
                                  event.bearing >= 0 &&
                                  event.bearing <= 360,
                              )
                              ?.map((event) => (
                                <small>{event.bearing}&#176; </small>
                              )) // Get the events arrays
                          }
                        </div>
                      </small>
                    </Row>
                    <Row className="p-0">
                      <Compass
                        width={200}
                        height={200}
                        degree={interferenceEventsQueue
                          ?.flat() // Flatten the array of arrays into a single array
                          ?.filter(
                            (event) =>
                              Object.keys(event).length > 0 &&
                              monitoredBands.length > 0 &&
                              event.frequencyValue >=
                                monitoredBands[0].centralFreq -
                                  monitoredBands[0].bandwidth / 2 &&
                              event.frequencyValue <=
                                monitoredBands[monitoredBands.length - 1]
                                  .centralFreq +
                                  monitoredBands[monitoredBands.length - 1] /
                                    2 &&
                              event.bearing >= 0 &&
                              event.bearing <= 360,
                          )
                          ?.map((event) => event.bearing)}
                      />
                    </Row>
                  </>
                )}
            </div>
          </Col>
        </Row>
      </Col>
      {isExpanded && (
        <Col style={{ width: "auto", maxWidth: 760 }}>
          <Row className="p-0 m-0">{renderIndicators()}</Row>
          <Row className="p-0 m-0">
            {interferenceAlerts?.length === 0 && renderNoAlerts()}
            {interferenceAlerts?.map((details, i) => {
              return (
                <InterferenceAlertCard
                  key={i}
                  alertDetails={details}
                  onViewMore={handleViewMore}
                  stationState={stationStatus.code}
                />
              );
            })}
          </Row>
        </Col>
      )}
    </Row>
  );
};

export default SpectrumTabs;
